/**
 * Master Workflow Dashboard Component
 *
 * DATA-DRIVEN DESIGN: All workflow configuration comes from the backend registry.
 * NO HARDCODED workflow definitions - steps, compliance domains, and program
 * variants are all loaded from the backend.
 *
 * When MN DHS changes hiring requirements, just update the JSON workflow
 * definition in the backend - no code changes needed.
 *
 * Features:
 * - Real-time sub-workflow progress tracking
 * - Dependency visualization between workflows
 * - MN DHS program variant support (EW, CFSS, DD Waiver)
 * - Compliance status indicators
 * - Instance hierarchy navigation
 *
 * @example
 * ```tsx
 * <MasterWorkflowDashboard
 *   masterInstanceId="master-001"
 *   programVariant="ELDERLY_WAIVER"
 * />
 * ```
 */

import React, { useMemo, useCallback } from 'react';
import {
  Card,
  Badge,
  Button,
  Text,
  Group,
  Stack,
  Alert,
  Loader,
  Progress,
  Grid,
  Paper,
  ThemeIcon,
  RingProgress,
  Timeline,
  Tooltip,
  ActionIcon,
  Menu,
  Divider,
  SimpleGrid,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconX,
  IconRefresh,
  IconUsers,
  IconUserPlus,
  IconCurrencyDollar,
  IconClipboardList,
  IconCash,
  IconChevronRight,
  IconDots,
  IconPlayerPlay,
  IconPlayerPause,
  IconArrowsShuffle,
  IconShieldCheck,
  IconAlertCircle,
  IconCircleCheck,
  IconCircleDashed,
  IconLoader,
  IconFile,
} from '@tabler/icons-react';
import {
  useSubWorkflowExecution,
  type ChildWorkflow,
  type SubWorkflowStatus,
} from '../hooks/useSubWorkflowExecution';
import {
  useMasterWorkflowConfig,
  type SubWorkflowConfig,
  type WorkflowCriticality,
} from '../hooks/useWorkflowRegistry';

/**
 * MN DHS Program variants supported
 */
export type MnDhsProgram =
  | 'ELDERLY_WAIVER'
  | 'CFSS'
  | 'DD_WAIVER'
  | 'CAC'
  | 'CADI'
  | 'BI_WAIVER'
  | 'PCA';

/**
 * Sub-workflow metadata for display (from backend or fallback)
 */
interface SubWorkflowMetadata {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  description: string;
  nodeId: string;
  criticality: WorkflowCriticality;
  complianceDomains: string[];
  estimatedSteps: number;
}

/**
 * Get icon for workflow based on its name/type
 * Icons are UI concern, so mapped here - but workflow data comes from backend
 */
function getWorkflowIcon(workflowName: string, iconHint?: string): React.ReactNode {
  // Check icon hint from backend first
  if (iconHint) {
    const iconMap: Record<string, React.ReactNode> = {
      'user-plus': <IconUserPlus size={20} />,
      'users': <IconUsers size={20} />,
      'currency-dollar': <IconCurrencyDollar size={20} />,
      'clipboard-list': <IconClipboardList size={20} />,
      'cash': <IconCash size={20} />,
    };
    if (iconMap[iconHint]) return iconMap[iconHint];
  }

  // Fallback: infer from workflow name
  if (workflowName.includes('hire') || workflowName.includes('employee')) {
    return <IconUserPlus size={20} />;
  }
  if (workflowName.includes('client') || workflowName.includes('signup')) {
    return <IconUsers size={20} />;
  }
  if (workflowName.includes('revenue') || workflowName.includes('cycle') || workflowName.includes('billing')) {
    return <IconCurrencyDollar size={20} />;
  }
  if (workflowName.includes('adl') || workflowName.includes('documentation') || workflowName.includes('clinical')) {
    return <IconClipboardList size={20} />;
  }
  if (workflowName.includes('payroll') || workflowName.includes('pay')) {
    return <IconCash size={20} />;
  }
  return <IconFile size={20} />;
}

/**
 * Transform backend SubWorkflowConfig to local SubWorkflowMetadata
 * This bridges backend data to UI-specific properties
 */
function toSubWorkflowMetadata(config: SubWorkflowConfig): SubWorkflowMetadata {
  return {
    id: config.workflowName,
    name: config.displayName,
    icon: getWorkflowIcon(config.workflowName, config.icon),
    color: config.color ?? 'gray',
    description: config.description ?? '',
    nodeId: config.id,
    criticality: config.criticality,
    complianceDomains: config.complianceDomains,
    estimatedSteps: config.estimatedSteps,
  };
}

/**
 * FALLBACK CONFIG - Only used when backend is unavailable during development
 * In production, this should NEVER be used - workflow config comes from backend
 */
const FALLBACK_SUB_WORKFLOW_CONFIG: SubWorkflowMetadata[] = [
  {
    id: 'wf_hire_employee_v1',
    name: 'Hire Employee',
    icon: <IconUserPlus size={20} />,
    color: 'blue',
    description: 'Complete employee onboarding from requisition to active in payroll',
    nodeId: 'MASTER_01_hire_employee',
    criticality: 'Operational',
    complianceDomains: ['EMPLOYMENT_LAW', 'DHS_TRAINING_REQUIREMENTS', 'MN_245C_BACKGROUND'],
    estimatedSteps: 8,
  },
  {
    id: 'wf_client_signup_v1',
    name: 'Client Sign Up',
    icon: <IconUsers size={20} />,
    color: 'green',
    description: 'Complete HCBS client intake from referral to active service recipient',
    nodeId: 'MASTER_02_client_signup',
    criticality: 'ComplianceCritical',
    complianceDomains: ['DHS_INTAKE', 'HIPAA', 'DHS_DOCUMENTATION'],
    estimatedSteps: 8,
  },
  {
    id: 'wf_revenue_cycle_v1',
    name: 'Revenue Cycle Management',
    icon: <IconCurrencyDollar size={20} />,
    color: 'yellow',
    description: 'Complete billing cycle from service to payment collection',
    nodeId: 'MASTER_03_revenue_cycle',
    criticality: 'FinanciallySignificant',
    complianceDomains: ['MEDICAID', 'MEDICAID_BILLING', 'MN_DHS_BILLING'],
    estimatedSteps: 10,
  },
  {
    id: 'wf_adl_documentation_v1',
    name: 'ADL Documentation',
    icon: <IconClipboardList size={20} />,
    color: 'cyan',
    description: 'Daily service documentation for Activities of Daily Living',
    nodeId: 'MASTER_04_adl_documentation',
    criticality: 'PatientImpact',
    complianceDomains: ['EVV', 'DHS_DOCUMENTATION', 'HIPAA'],
    estimatedSteps: 9,
  },
  {
    id: 'wf_payroll_service_rendering_v1',
    name: 'Payroll Processing',
    icon: <IconCash size={20} />,
    color: 'grape',
    description: 'Process employee time to payroll based on service delivery',
    nodeId: 'MASTER_05_payroll',
    criticality: 'FinanciallySignificant',
    complianceDomains: ['MN_WAGE_HOUR', 'EVV'],
    estimatedSteps: 10,
  },
];

/**
 * Program-specific configurations
 */
const PROGRAM_CONFIG: Record<MnDhsProgram, { name: string; color: string; supervision: string }> = {
  ELDERLY_WAIVER: { name: 'Elderly Waiver (EW)', color: 'blue', supervision: 'RN Monthly' },
  CFSS: { name: 'Community First Services (CFSS)', color: 'green', supervision: 'QP Quarterly' },
  DD_WAIVER: { name: 'DD Waiver', color: 'violet', supervision: 'DC Per Plan' },
  CAC: { name: 'Community Alternative Care (CAC)', color: 'cyan', supervision: 'RN Monthly' },
  CADI: { name: 'CADI Waiver', color: 'teal', supervision: 'Varies' },
  BI_WAIVER: { name: 'Brain Injury Waiver', color: 'pink', supervision: 'Specialist' },
  PCA: { name: 'Personal Care Assistance (PCA)', color: 'orange', supervision: 'QP Quarterly' },
};

/**
 * Component props
 */
export interface MasterWorkflowDashboardProps {
  /** Master workflow instance ID */
  masterInstanceId: string;
  /** MN DHS program variant */
  programVariant?: MnDhsProgram;
  /** Tenant ID (for multi-tenant) */
  tenantId?: string;
  /** Callback when sub-workflow is selected */
  onSubWorkflowSelect?: (workflowId: string, instanceId: string) => void;
  /** Callback for manual workflow start */
  onStartSubWorkflow?: (workflowName: string) => void;
  /** Show compact view */
  compact?: boolean;
}

/**
 * Get status color for badge
 */
function getStatusColor(status: SubWorkflowStatus): string {
  switch (status) {
    case 'Completed':
      return 'green';
    case 'Failed':
      return 'red';
    case 'Running':
      return 'blue';
    case 'Cancelled':
      return 'gray';
    case 'Pending':
    default:
      return 'gray';
  }
}

/**
 * Get status icon
 */
function getStatusIcon(status: SubWorkflowStatus): React.ReactNode {
  switch (status) {
    case 'Completed':
      return <IconCircleCheck size={16} />;
    case 'Failed':
      return <IconX size={16} />;
    case 'Running':
      return <IconLoader size={16} className="spin-animation" />;
    case 'Cancelled':
      return <IconAlertCircle size={16} />;
    case 'Pending':
    default:
      return <IconCircleDashed size={16} />;
  }
}

/**
 * Get criticality color
 */
function getCriticalityColor(criticality: string): string {
  switch (criticality) {
    case 'PatientImpact':
      return 'red';
    case 'ComplianceCritical':
      return 'orange';
    case 'FinanciallySignificant':
      return 'yellow';
    case 'Operational':
    default:
      return 'blue';
  }
}

/**
 * Sub-workflow card component
 */
interface SubWorkflowCardProps {
  config: SubWorkflowMetadata;
  childWorkflow?: ChildWorkflow;
  programVariant?: MnDhsProgram;
  onSelect?: () => void;
  onStart?: () => void;
  onCancel?: () => void;
  compact?: boolean;
}

function SubWorkflowCard({
  config,
  childWorkflow,
  programVariant,
  onSelect,
  onStart,
  onCancel,
  compact,
}: SubWorkflowCardProps) {
  const status: SubWorkflowStatus = childWorkflow?.status ?? 'Pending';
  const progress = childWorkflow?.progressPercent ?? 0;
  const completedTasks = childWorkflow?.completedSteps ?? 0;
  const totalTasks = childWorkflow?.totalSteps ?? config.estimatedSteps;

  return (
    <Paper
      shadow="sm"
      p={compact ? 'sm' : 'md'}
      radius="md"
      withBorder
      style={{ cursor: onSelect ? 'pointer' : 'default' }}
      onClick={onSelect}
    >
      <Group justify="space-between" mb={compact ? 'xs' : 'sm'}>
        <Group gap="xs">
          <ThemeIcon color={config.color} size={compact ? 'sm' : 'md'} radius="md">
            {config.icon}
          </ThemeIcon>
          <div>
            <Text size={compact ? 'xs' : 'sm'} fw={600}>
              {config.name}
            </Text>
            {!compact && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {config.description}
              </Text>
            )}
          </div>
        </Group>
        <Group gap="xs">
          <Badge
            color={getStatusColor(status)}
            size={compact ? 'xs' : 'sm'}
            leftSection={getStatusIcon(status)}
          >
            {status}
          </Badge>
          {!compact && (
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <ActionIcon variant="subtle" size="sm" onClick={(e) => e.stopPropagation()}>
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {status === 'Pending' && (
                  <Menu.Item
                    leftSection={<IconPlayerPlay size={14} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onStart?.();
                    }}
                  >
                    Start Workflow
                  </Menu.Item>
                )}
                {status === 'Running' && (
                  <Menu.Item
                    leftSection={<IconPlayerPause size={14} />}
                    color="yellow"
                  >
                    Pause Workflow
                  </Menu.Item>
                )}
                {status === 'Running' && (
                  <Menu.Item
                    leftSection={<IconX size={14} />}
                    color="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCancel?.();
                    }}
                  >
                    Cancel Workflow
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>

      <Progress
        value={progress}
        color={getStatusColor(status)}
        size={compact ? 'xs' : 'sm'}
        radius="xl"
        mb={compact ? 'xs' : 'sm'}
      />

      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          {completedTasks}/{totalTasks} tasks
        </Text>
        <Group gap={4}>
          <Tooltip label={`Criticality: ${config.criticality}`}>
            <Badge size="xs" color={getCriticalityColor(config.criticality)} variant="light">
              {config.criticality.replace('Critical', '')}
            </Badge>
          </Tooltip>
          {programVariant && (
            <Tooltip label={`Supervision: ${PROGRAM_CONFIG[programVariant].supervision}`}>
              <Badge size="xs" color="gray" variant="light">
                <IconShieldCheck size={10} style={{ marginRight: 2 }} />
                {PROGRAM_CONFIG[programVariant].supervision.split(' ')[0]}
              </Badge>
            </Tooltip>
          )}
        </Group>
      </Group>

      {/* Compliance domains */}
      {!compact && (
        <Group gap={4} mt="xs">
          {config.complianceDomains.slice(0, 3).map((domain) => (
            <Badge key={domain} size="xs" variant="dot" color="gray">
              {domain.replace(/_/g, ' ')}
            </Badge>
          ))}
          {config.complianceDomains.length > 3 && (
            <Tooltip label={config.complianceDomains.slice(3).join(', ')}>
              <Badge size="xs" variant="dot" color="gray">
                +{config.complianceDomains.length - 3}
              </Badge>
            </Tooltip>
          )}
        </Group>
      )}
    </Paper>
  );
}

/**
 * Dependency visualization
 */
function DependencyArrow({ from, to }: { from: string; to: string }) {
  return (
    <Group gap={4} justify="center" py="xs">
      <Text size="xs" c="dimmed">
        {from}
      </Text>
      <IconChevronRight size={12} color="gray" />
      <Text size="xs" c="dimmed">
        {to}
      </Text>
    </Group>
  );
}

/**
 * Master Workflow Dashboard Component
 */
export function MasterWorkflowDashboard({
  masterInstanceId,
  programVariant = 'ELDERLY_WAIVER',
  tenantId,
  onSubWorkflowSelect,
  onStartSubWorkflow,
  compact = false,
}: MasterWorkflowDashboardProps) {
  // Load workflow configuration from backend (data-driven)
  const {
    config: masterConfig,
    loading: configLoading,
    error: configError,
  } = useMasterWorkflowConfig({
    tenantId: tenantId ?? 'default',
    programVariant,
  });

  // Transform backend config to UI metadata, with fallback
  const SUB_WORKFLOW_CONFIG: SubWorkflowMetadata[] = useMemo(() => {
    if (masterConfig?.subWorkflows && masterConfig.subWorkflows.length > 0) {
      return masterConfig.subWorkflows.map(toSubWorkflowMetadata);
    }
    // Fallback when backend is unavailable (dev mode only)
    console.warn('Using fallback workflow config - backend registry not available');
    return FALLBACK_SUB_WORKFLOW_CONFIG;
  }, [masterConfig?.subWorkflows]);

  // Get program config from backend or fallback
  const program = useMemo(() => {
    if (masterConfig?.subWorkflows && masterConfig.subWorkflows.length > 0) {
      // Try to get from backend program config
      const programConfig = PROGRAM_CONFIG[programVariant];
      return programConfig ?? { name: programVariant, color: 'gray', supervision: 'Per Plan' };
    }
    return PROGRAM_CONFIG[programVariant];
  }, [masterConfig, programVariant]);

  const {
    childWorkflows,
    aggregatedProgress,
    hierarchy,
    startSubWorkflow,
    cancelSubWorkflow,
    loading: executionLoading,
    error: executionError,
    refetchChildren,
    isAnyChildRunning: isAnyRunning,
    allChildrenComplete: isAllCompleted,
    isAnyChildFailed: isAnyFailed,
  } = useSubWorkflowExecution({
    parentInstanceId: masterInstanceId,
    pollingInterval: 5000,
  });

  // Compute derived values from aggregatedProgress
  const overallProgress = aggregatedProgress?.overallProgressPercent ?? 0;
  const completedCount = aggregatedProgress?.completedChildren ?? 0;
  const failedCount = aggregatedProgress?.failedChildren ?? 0;
  const runningCount = aggregatedProgress?.runningChildren ?? 0;
  const refetch = refetchChildren;

  // Combined loading state
  const loading = configLoading || executionLoading;
  const error = configError ?? executionError;

  // Map child workflows to config
  const workflowMap = useMemo(() => {
    const map = new Map<string, ChildWorkflow>();
    childWorkflows.forEach((cw) => {
      map.set(cw.workflowName, cw);
    });
    return map;
  }, [childWorkflows]);

  // Handle start sub-workflow
  const handleStartSubWorkflow = useCallback(
    async (config: SubWorkflowMetadata) => {
      await startSubWorkflow({
        parentNodeId: config.nodeId,
        workflowName: config.id,
        workflowVersion: '1.0.0',
        executionMode: 'Async',
        failureMode: 'RetryChild',
        inputData: {
          programVariant,
          complianceDomains: config.complianceDomains,
        },
      });
      onStartSubWorkflow?.(config.id);
    },
    [masterInstanceId, programVariant, startSubWorkflow, onStartSubWorkflow]
  );

  // Handle cancel sub-workflow
  const handleCancelSubWorkflow = useCallback(
    async (instanceId: string) => {
      await cancelSubWorkflow(instanceId, 'User requested cancellation');
    },
    [cancelSubWorkflow]
  );

  if (loading && childWorkflows.length === 0) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="center" py="xl">
          <Loader size="md" />
          <Text>Loading Master Workflow data...</Text>
        </Group>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="Error Loading Workflow Data"
        color="red"
        variant="filled"
      >
        {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Card shadow="sm" padding={compact ? 'sm' : 'lg'} radius="md" withBorder>
        <Group justify="space-between" mb={compact ? 'xs' : 'md'}>
          <Group gap="sm">
            <Text fw={700} size={compact ? 'md' : 'lg'}>
              Master Business Operations
            </Text>
            <Badge color={program.color} size={compact ? 'sm' : 'md'}>
              {program.name}
            </Badge>
          </Group>
          <Group gap="xs">
            <Button
              variant="light"
              size="xs"
              leftSection={<IconRefresh size={14} />}
              onClick={() => refetch()}
              loading={loading}
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Overall Progress */}
        <Grid gutter={compact ? 'xs' : 'md'}>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper p="md" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>
                  Overall Progress
                </Text>
                <RingProgress
                  size={compact ? 48 : 64}
                  thickness={compact ? 4 : 6}
                  sections={[
                    { value: overallProgress, color: isAnyFailed ? 'red' : isAllCompleted ? 'green' : 'blue' },
                  ]}
                  label={
                    <Text size="xs" ta="center" fw={700}>
                      {Math.round(overallProgress)}%
                    </Text>
                  }
                />
              </Group>
              <Group gap="xs">
                <Badge color="green" variant="light" size="sm">
                  {completedCount} Complete
                </Badge>
                <Badge color="blue" variant="light" size="sm">
                  {runningCount} Running
                </Badge>
                {failedCount > 0 && (
                  <Badge color="red" variant="light" size="sm">
                    {failedCount} Failed
                  </Badge>
                )}
              </Group>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper p="md" radius="md" withBorder>
              <Text size="sm" fw={500} mb="xs">
                Aggregated Tasks
              </Text>
              {aggregatedProgress ? (
                <>
                  <Progress
                    value={
                      aggregatedProgress.totalChildren > 0
                        ? (aggregatedProgress.completedChildren / aggregatedProgress.totalChildren) * 100
                        : 0
                    }
                    color="blue"
                    size="lg"
                    radius="xl"
                    mb="xs"
                  />
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">
                      {aggregatedProgress.completedChildren} / {aggregatedProgress.totalChildren} children
                    </Text>
                    <Text size="xs" c="dimmed">
                      {aggregatedProgress.totalChildren - aggregatedProgress.completedChildren - aggregatedProgress.failedChildren} pending
                    </Text>
                  </Group>
                </>
              ) : (
                <Text size="sm" c="dimmed">
                  No task data available
                </Text>
              )}
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper p="md" radius="md" withBorder>
              <Text size="sm" fw={500} mb="xs">
                Compliance Status
              </Text>
              <Group gap="xs">
                <Tooltip label="EVV Compliance">
                  <ThemeIcon
                    color={isAnyFailed ? 'red' : isAllCompleted ? 'green' : 'gray'}
                    size="sm"
                    radius="xl"
                  >
                    <IconShieldCheck size={14} />
                  </ThemeIcon>
                </Tooltip>
                <Tooltip label="HIPAA Compliance">
                  <ThemeIcon
                    color={isAllCompleted ? 'green' : 'gray'}
                    size="sm"
                    radius="xl"
                  >
                    <IconShieldCheck size={14} />
                  </ThemeIcon>
                </Tooltip>
                <Tooltip label="MN DHS Requirements">
                  <ThemeIcon
                    color={isAllCompleted ? 'green' : 'gray'}
                    size="sm"
                    radius="xl"
                  >
                    <IconShieldCheck size={14} />
                  </ThemeIcon>
                </Tooltip>
              </Group>
              <Text size="xs" c="dimmed" mt="xs">
                Supervision: {program.supervision}
              </Text>
            </Paper>
          </Grid.Col>
        </Grid>
      </Card>

      {/* Sub-workflow Cards */}
      <Card shadow="sm" padding={compact ? 'sm' : 'lg'} radius="md" withBorder>
        <Text fw={600} size="md" mb="md">
          Sub-Workflows ({SUB_WORKFLOW_CONFIG.length})
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: compact ? 3 : 2 }} spacing="md">
          {SUB_WORKFLOW_CONFIG.map((config) => {
            const childWorkflow = workflowMap.get(config.id);
            return (
              <SubWorkflowCard
                key={config.id}
                config={config}
                childWorkflow={childWorkflow}
                programVariant={programVariant}
                onSelect={
                  childWorkflow
                    ? () => onSubWorkflowSelect?.(config.id, childWorkflow.id)
                    : undefined
                }
                onStart={() => handleStartSubWorkflow(config)}
                onCancel={
                  childWorkflow ? () => handleCancelSubWorkflow(childWorkflow.id) : undefined
                }
                compact={compact}
              />
            );
          })}
        </SimpleGrid>
      </Card>

      {/* Dependency Flow */}
      {!compact && (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text fw={600} size="md" mb="md">
            Workflow Dependencies
          </Text>
          <Timeline active={completedCount} bulletSize={24} lineWidth={2}>
            <Timeline.Item
              bullet={
                workflowMap.get('wf_hire_employee_v1')?.status === 'Completed' ? (
                  <IconCheck size={12} />
                ) : (
                  <IconClock size={12} />
                )
              }
              title="Phase 1: Setup"
            >
              <Text size="sm" c="dimmed">
                Hire Employee + Client Sign Up (parallel)
              </Text>
              <Group gap="xs" mt="xs">
                <Badge
                  color={getStatusColor(workflowMap.get('wf_hire_employee_v1')?.status ?? 'Pending')}
                  size="xs"
                >
                  Hire Employee
                </Badge>
                <Badge
                  color={getStatusColor(workflowMap.get('wf_client_signup_v1')?.status ?? 'Pending')}
                  size="xs"
                >
                  Client Sign Up
                </Badge>
              </Group>
            </Timeline.Item>

            <Timeline.Item
              bullet={
                workflowMap.get('wf_adl_documentation_v1')?.status === 'Completed' ? (
                  <IconCheck size={12} />
                ) : (
                  <IconClock size={12} />
                )
              }
              title="Phase 2: Service Delivery"
            >
              <Text size="sm" c="dimmed">
                ADL Documentation (requires Client Sign Up)
              </Text>
              <Group gap="xs" mt="xs">
                <Badge
                  color={getStatusColor(
                    workflowMap.get('wf_adl_documentation_v1')?.status ?? 'Pending'
                  )}
                  size="xs"
                >
                  ADL Documentation
                </Badge>
              </Group>
            </Timeline.Item>

            <Timeline.Item
              bullet={
                workflowMap.get('wf_revenue_cycle_v1')?.status === 'Completed' &&
                workflowMap.get('wf_payroll_service_rendering_v1')?.status === 'Completed' ? (
                  <IconCheck size={12} />
                ) : (
                  <IconClock size={12} />
                )
              }
              title="Phase 3: Financial Processing"
            >
              <Text size="sm" c="dimmed">
                Revenue Cycle + Payroll (parallel, requires ADL)
              </Text>
              <Group gap="xs" mt="xs">
                <Badge
                  color={getStatusColor(workflowMap.get('wf_revenue_cycle_v1')?.status ?? 'Pending')}
                  size="xs"
                >
                  Revenue Cycle
                </Badge>
                <Badge
                  color={getStatusColor(
                    workflowMap.get('wf_payroll_service_rendering_v1')?.status ?? 'Pending'
                  )}
                  size="xs"
                >
                  Payroll
                </Badge>
              </Group>
            </Timeline.Item>

            <Timeline.Item
              bullet={isAllCompleted ? <IconCheck size={12} /> : <IconClock size={12} />}
              title="Complete"
            >
              <Text size="sm" c="dimmed">
                All business operations complete
              </Text>
            </Timeline.Item>
          </Timeline>
        </Card>
      )}

      {/* Instance Hierarchy */}
      {!compact && hierarchy && (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text fw={600} size="md" mb="md">
            Instance Hierarchy
          </Text>
          <Paper p="md" radius="md" bg="gray.0" withBorder>
            <Group gap="xs">
              <Text size="sm" fw={500}>
                Root: {hierarchy.instanceId}
              </Text>
              <Badge color="blue" size="sm">
                {hierarchy.status}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed" mt="xs">
              Workflow: {hierarchy.workflowName} v{hierarchy.workflowVersion}
            </Text>
            {hierarchy.children && hierarchy.children.length > 0 && (
              <Stack gap="xs" mt="md" pl="md" style={{ borderLeft: '2px solid var(--mantine-color-blue-3)' }}>
                {hierarchy.children.map((child) => (
                  <Group key={child.instanceId} gap="xs">
                    <Text size="xs">{child.workflowName}</Text>
                    <Badge color={getStatusColor(child.status)} size="xs">
                      {child.status}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      ({child.instanceId.slice(0, 8)}...)
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
          </Paper>
        </Card>
      )}

      {/* Alerts */}
      {isAnyFailed && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Sub-Workflow Failure Detected"
          color="red"
          variant="light"
        >
          One or more sub-workflows have failed. Review the failed workflows and take corrective
          action. This may require compensation procedures.
        </Alert>
      )}

      {isAllCompleted && (
        <Alert
          icon={<IconCheck size={16} />}
          title="Master Workflow Complete"
          color="green"
          variant="light"
        >
          All 5 sub-workflows have completed successfully. Business operations are fully processed
          for this cycle.
        </Alert>
      )}
    </Stack>
  );
}

export default MasterWorkflowDashboard;
